import PropTypes from 'prop-types';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import useTableFilter from '../hooks/useTableFilter';
import { getWithdrawals } from '../services/financial';
import { DEFAULT_PRODUCT_CURRENCY_CODE } from '../utils/currency';

export const WithdrawalsContext = createContext({
  table: {},
  withdrawals: [],
  fetching: false,
  isInitialLoading: false,
  refreshWithdrawals: async () => {},
  withdrawalsUpdatedAt: 0,
  count: 0,
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
});

export const WithdrawalsStatus = {
  pending: {
    label: 'Pendente',
    color: 'warning',
  },
  approved: {
    label: 'Aprovado',
    color: 'success',
  },
  rejected: {
    label: 'Rejeitado',
    color: 'error',
  },
};

const WithdrawalsProvider = ({ children }) => {
  const table = useTableFilter({
    defaultCurrentPage: 1,
  });
  const [currency, setCurrency] = useState(DEFAULT_PRODUCT_CURRENCY_CODE);

  const handleSetCurrency = useCallback((nextCurrency) => {
    const resolvedCurrency =
      nextCurrency != null && String(nextCurrency).trim() !== ''
        ? String(nextCurrency).trim().toUpperCase()
        : DEFAULT_PRODUCT_CURRENCY_CODE;

    setCurrency(resolvedCurrency);
  }, []);

  const {
    data: { results: withdrawals = [], count },
    isLoading: fetching,
    isFetching,
    isFetchedAfterMount,
    refetch: refreshWithdrawals,
    dataUpdatedAt: withdrawalsUpdatedAt,
  } = useQuery({
    queryKey: ['withdrawals', currency, table.page, table.search, table.filter],
    queryFn: () =>
      getWithdrawals({
        page: table.page,
        search: table.search,
        start_date: table.filter?.start_date || subDays(new Date(), 90),
        end_date: table.filter?.end_date || new Date(),
        currency,
      }),
    initialData: {
      results: [],
      count: 0,
    },
  });

  const isInitialLoading = isFetching && !isFetchedAfterMount;

  const value = useMemo(
    () => ({
      withdrawals,
      table,
      fetching,
      isInitialLoading,
      refreshWithdrawals,
      withdrawalsUpdatedAt,
      count,
      currency,
      setCurrency: handleSetCurrency,
    }),
    [
      withdrawals,
      table,
      fetching,
      isInitialLoading,
      refreshWithdrawals,
      withdrawalsUpdatedAt,
      count,
      currency,
      handleSetCurrency,
    ]
  );

  return <WithdrawalsContext.Provider value={value}>{children}</WithdrawalsContext.Provider>;
};

WithdrawalsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default WithdrawalsProvider;
